<template>
  <div class="space-y-6">
    <!-- Date Range Selector + Sync -->
    <div class="flex items-center justify-between">
      <div class="flex flex-wrap gap-2">
        <button
          v-for="preset in datePresets"
          :key="preset.value"
          @click="selectDateRange(preset.value)"
          :class="selectedPreset === preset.value
            ? 'bg-primary-500 text-white'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'"
          class="px-3 py-1.5 text-sm font-medium rounded-lg">
          {{ preset.label }}
        </button>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="summary.metrics_synced_at" class="text-xs text-gray-500 dark:text-gray-400">
          Last synced {{ formatDate(summary.metrics_synced_at) }}
        </span>
        <button
          @click="syncMetrics"
          :disabled="syncing"
          class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
          <svg v-if="syncing" class="animate-spin -ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Sync
        </button>
      </div>
    </div>

    <!-- Period Labels -->
    <div v-if="comparison.period_label" class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
      <span class="font-medium text-gray-700 dark:text-gray-300">{{ comparison.period_label }}</span>
      <span>vs</span>
      <span>{{ comparison.comparison_label }}</span>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div v-for="card in summaryCards" :key="card.label"
        class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {{ card.label }}
        </dt>
        <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {{ card.value.toLocaleString() }}
        </dd>
        <div v-if="card.delta !== null" class="mt-2 flex items-center gap-1.5">
          <span
            :class="card.delta > 0 ? 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/30'
              : card.delta < 0 ? 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
                : 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium">
            <template v-if="card.delta > 0">&uarr;</template>
            <template v-else-if="card.delta < 0">&darr;</template>
            <template v-else>&mdash;</template>
            {{ Math.abs(card.delta).toFixed(1) }}%
          </span>
          <span class="text-xs text-gray-400 dark:text-gray-500">
            vs {{ card.prev.toLocaleString() }}
          </span>
        </div>
        <div v-else-if="card.prev === 0 && card.value > 0" class="mt-2">
          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/30">
            New
          </span>
        </div>
      </div>
    </div>

    <!-- Impressions Area Chart -->
    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Impressions Over Time
      </h3>
      <div ref="impressionsChart" class="w-full relative" style="height: 300px;" />
    </div>

    <!-- Engagement Metrics Chart -->
    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Engagement Metrics
      </h3>
      <div ref="engagementChart" class="w-full relative" style="height: 260px;" />
    </div>

    <!-- Impressions Breakdown (Desktop vs Mobile) -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Search vs Maps
        </h3>
        <div ref="pieChart" class="w-full flex justify-center" style="height: 240px;" />
      </div>
      <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Desktop vs Mobile
        </h3>
        <div ref="deviceChart" class="w-full flex justify-center" style="height: 240px;" />
      </div>
    </div>

    <!-- Metrics Table -->
    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-900/50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Date
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Search
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Maps
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Directions
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Calls
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Website
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="metric in metrics" :key="metric.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/30">
            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
              {{ metric.date }}
            </td>
            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
              {{ metric.total_search_impressions }}
            </td>
            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
              {{ metric.total_maps_impressions }}
            </td>
            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
              {{ metric.business_direction_requests }}
            </td>
            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
              {{ metric.call_clicks }}
            </td>
            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
              {{ metric.website_clicks }}
            </td>
          </tr>
          <tr v-if="!loading && metrics.length === 0">
            <td colspan="6" class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No metrics data yet. Click "Sync" to fetch from LocalViking.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import * as d3 from 'd3'
import { getLocationMetrics, syncLocationMetrics } from '~/javascript/api/location_metrics.js'

const props = defineProps({
  locationId: { type: String, required: true }
})

const metrics = ref([])
const summary = ref({})
const comparison = ref({})
const loading = ref(false)
const syncing = ref(false)
const selectedPreset = ref('30d')

const impressionsChart = ref(null)
const engagementChart = ref(null)
const pieChart = ref(null)
const deviceChart = ref(null)

const datePresets = [
  { label: 'Last 7d', value: '7d' },
  { label: 'Last 30d', value: '30d' },
  { label: 'Last 90d', value: '90d' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'This Quarter', value: 'this_quarter' },
  { label: 'Last Quarter', value: 'last_quarter' }
]

const summaryCards = computed(() => {
  const cards = [
    { label: 'Total Impressions', key: 'total_impressions' },
    { label: 'Direction Requests', key: 'total_direction_requests' },
    { label: 'Phone Calls', key: 'total_call_clicks' },
    { label: 'Website Clicks', key: 'total_website_clicks' }
  ]
  return cards.map((card) => {
    const current = summary.value[card.key] || 0
    const prev = comparison.value[card.key] || 0
    const delta = prev > 0 ? ((current - prev) / prev * 100) : null
    return { ...card, value: current, prev, delta }
  })
})

const selectedDaysCount = computed(() => {
  const map = { '7d': 7, '30d': 30, '90d': 90, 'this_month': 31, 'last_month': 31, 'this_quarter': 90, 'last_quarter': 90 }
  return map[selectedPreset.value] || 30
})

const COLORS = {
  search: '#304f95',
  maps: '#6b8dd6',
  directions: '#10b981',
  calls: '#f59e0b',
  website: '#8b5cf6',
  searchFill: 'rgba(48, 79, 149, 0.15)',
  mapsFill: 'rgba(107, 141, 214, 0.15)',
  desktop: '#304f95',
  mobile: '#6b8dd6',
  desktopSearch: '#304f95',
  desktopMaps: '#6b8dd6',
  mobileSearch: '#10b981',
  mobileMaps: '#f59e0b'
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const getTickInterval = () => {
  if (selectedDaysCount.value <= 7) return d3.timeDay.every(1)
  if (selectedDaysCount.value <= 30) return d3.timeWeek.every(1)
  return d3.timeMonth.every(1)
}

const renderImpressionsChart = () => {
  const container = impressionsChart.value
  if (!container) return

  d3.select(container).selectAll('*').remove()

  const data = [...metrics.value]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      search: d.total_search_impressions || 0,
      maps: d.total_maps_impressions || 0
    }))

  const margin = { top: 20, right: 30, bottom: 40, left: 50 }
  const width = container.clientWidth - margin.left - margin.right
  const height = 300 - margin.top - margin.bottom

  const svg = d3.select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  const x = d3.scaleTime()
    .domain(d3.extent(data, (d) => d.date))
    .range([0, width])

  const y = d3.scaleLinear()
    .domain([0, d3.max(data, (d) => Math.max(d.search, d.maps)) * 1.1])
    .nice()
    .range([height, 0])

  // Gradient for search area
  const defs = svg.append('defs')

  const searchGradient = defs.append('linearGradient')
    .attr('id', 'searchGradient')
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%')
  searchGradient.append('stop').attr('offset', '0%').attr('stop-color', COLORS.search).attr('stop-opacity', 0.3)
  searchGradient.append('stop').attr('offset', '100%').attr('stop-color', COLORS.search).attr('stop-opacity', 0.02)

  const mapsGradient = defs.append('linearGradient')
    .attr('id', 'mapsGradient')
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%')
  mapsGradient.append('stop').attr('offset', '0%').attr('stop-color', COLORS.maps).attr('stop-opacity', 0.3)
  mapsGradient.append('stop').attr('offset', '100%').attr('stop-color', COLORS.maps).attr('stop-opacity', 0.02)

  // Grid lines
  svg.append('g')
    .attr('class', 'grid')
    .call(d3.axisLeft(y).tickSize(-width).tickFormat(''))
    .selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-dasharray', '3,3')
  svg.selectAll('.grid .domain').remove()

  // Search area
  const searchArea = d3.area()
    .x((d) => x(d.date))
    .y0(height)
    .y1((d) => y(d.search))
    .curve(d3.curveMonotoneX)

  svg.append('path')
    .datum(data)
    .attr('fill', 'url(#searchGradient)')
    .attr('d', searchArea)

  // Maps area
  const mapsArea = d3.area()
    .x((d) => x(d.date))
    .y0(height)
    .y1((d) => y(d.maps))
    .curve(d3.curveMonotoneX)

  svg.append('path')
    .datum(data)
    .attr('fill', 'url(#mapsGradient)')
    .attr('d', mapsArea)

  // Search line
  const searchLine = d3.line()
    .x((d) => x(d.date))
    .y((d) => y(d.search))
    .curve(d3.curveMonotoneX)

  svg.append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', COLORS.search)
    .attr('stroke-width', 2.5)
    .attr('d', searchLine)

  // Maps line
  const mapsLine = d3.line()
    .x((d) => x(d.date))
    .y((d) => y(d.maps))
    .curve(d3.curveMonotoneX)

  svg.append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', COLORS.maps)
    .attr('stroke-width', 2.5)
    .attr('d', mapsLine)

  // Axes
  const tickCount = getTickInterval()

  svg.append('g')
    .attr('transform', `translate(0,${height})`)
    .call(d3.axisBottom(x).ticks(tickCount).tickFormat(d3.timeFormat('%b %d')))
    .selectAll('text')
    .attr('fill', '#6b7280')
    .attr('font-size', '11px')

  svg.append('g')
    .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format(',')))
    .selectAll('text')
    .attr('fill', '#6b7280')
    .attr('font-size', '11px')

  svg.selectAll('.domain').attr('stroke', '#e5e7eb')
  svg.selectAll('.tick line').attr('stroke', '#e5e7eb')

  // Legend
  const legend = svg.append('g').attr('transform', `translate(${width - 180}, -10)`)

  legend.append('line').attr('x1', 0).attr('x2', 20).attr('y1', 6)
    .attr('y2', 6)
    .attr('stroke', COLORS.search)
    .attr('stroke-width', 2.5)
  legend.append('text').attr('x', 26).attr('y', 10).text('Search')
    .attr('fill', '#6b7280')
    .attr('font-size', '12px')

  legend.append('line').attr('x1', 90).attr('x2', 110).attr('y1', 6)
    .attr('y2', 6)
    .attr('stroke', COLORS.maps)
    .attr('stroke-width', 2.5)
  legend.append('text').attr('x', 116).attr('y', 10).text('Maps')
    .attr('fill', '#6b7280')
    .attr('font-size', '12px')

  // Tooltip
  const tooltip = d3.select(container)
    .append('div')
    .style('position', 'absolute')
    .style('background', 'rgba(17, 24, 39, 0.9)')
    .style('color', 'white')
    .style('padding', '8px 12px')
    .style('border-radius', '8px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('opacity', 0)
    .style('z-index', 10)

  const bisect = d3.bisector((d) => d.date).left

  svg.append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'transparent')
    .on('mousemove', (event) => {
      const [mx] = d3.pointer(event)
      const x0 = x.invert(mx)
      const i = bisect(data, x0, 1)
      const d0 = data[i - 1]
      const d1 = data[i]
      if (!d0 || !d1) return
      const d = x0 - d0.date > d1.date - x0 ? d1 : d0

      tooltip
        .style('opacity', 1)
        .html(`<strong>${d3.timeFormat('%b %d, %Y')(d.date)}</strong><br/>Search: ${d.search.toLocaleString()}<br/>Maps: ${d.maps.toLocaleString()}`)
        .style('left', `${x(d.date) + margin.left + 10}px`)
        .style('top', `${y(Math.max(d.search, d.maps)) + margin.top - 10}px`)
    })
    .on('mouseleave', () => tooltip.style('opacity', 0))
}

const renderEngagementChart = () => {
  const container = engagementChart.value
  if (!container) return

  d3.select(container).selectAll('*').remove()

  const data = [...metrics.value]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      directions: d.business_direction_requests || 0,
      calls: d.call_clicks || 0,
      website: d.website_clicks || 0
    }))

  const margin = { top: 20, right: 30, bottom: 40, left: 50 }
  const width = container.clientWidth - margin.left - margin.right
  const height = 260 - margin.top - margin.bottom

  const svg = d3.select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  const keys = ['directions', 'calls', 'website']
  const colorMap = { directions: COLORS.directions, calls: COLORS.calls, website: COLORS.website }
  const labelMap = { directions: 'Directions', calls: 'Calls', website: 'Website' }

  const stack = d3.stack().keys(keys).order(d3.stackOrderNone).offset(d3.stackOffsetNone)
  const series = stack(data)

  const x = d3.scaleTime()
    .domain(d3.extent(data, (d) => d.date))
    .range([0, width])

  const y = d3.scaleLinear()
    .domain([0, d3.max(series, (s) => d3.max(s, (d) => d[1])) * 1.1])
    .nice()
    .range([height, 0])

  // Grid
  svg.append('g')
    .call(d3.axisLeft(y).tickSize(-width).tickFormat(''))
    .selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-dasharray', '3,3')
  svg.selectAll('.grid .domain').remove()

  // Stacked areas
  const area = d3.area()
    .x((d) => x(d.data.date))
    .y0((d) => y(d[0]))
    .y1((d) => y(d[1]))
    .curve(d3.curveMonotoneX)

  svg.selectAll('.layer')
    .data(series)
    .join('path')
    .attr('class', 'layer')
    .attr('fill', (d) => colorMap[d.key])
    .attr('fill-opacity', 0.6)
    .attr('stroke', (d) => colorMap[d.key])
    .attr('stroke-width', 1.5)
    .attr('d', area)

  // Axes
  const tickCount = getTickInterval()

  svg.append('g')
    .attr('transform', `translate(0,${height})`)
    .call(d3.axisBottom(x).ticks(tickCount).tickFormat(d3.timeFormat('%b %d')))
    .selectAll('text')
    .attr('fill', '#6b7280')
    .attr('font-size', '11px')

  svg.append('g')
    .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format(',')))
    .selectAll('text')
    .attr('fill', '#6b7280')
    .attr('font-size', '11px')

  svg.selectAll('.domain').attr('stroke', '#e5e7eb')
  svg.selectAll('.tick line').attr('stroke', '#e5e7eb')

  // Legend
  const legend = svg.append('g').attr('transform', `translate(${width - 280}, -10)`)
  keys.forEach((key, i) => {
    const g = legend.append('g').attr('transform', `translate(${i * 95}, 0)`)
    g.append('rect').attr('width', 12).attr('height', 12).attr('rx', 3)
      .attr('fill', colorMap[key])
      .attr('fill-opacity', 0.6)
    g.append('text').attr('x', 17).attr('y', 10).text(labelMap[key])
      .attr('fill', '#6b7280')
      .attr('font-size', '12px')
  })

  // Tooltip
  const tooltip = d3.select(container)
    .append('div')
    .style('position', 'absolute')
    .style('background', 'rgba(17, 24, 39, 0.9)')
    .style('color', 'white')
    .style('padding', '8px 12px')
    .style('border-radius', '8px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('opacity', 0)
    .style('z-index', 10)

  const bisect = d3.bisector((d) => d.date).left

  svg.append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'transparent')
    .on('mousemove', (event) => {
      const [mx] = d3.pointer(event)
      const x0 = x.invert(mx)
      const i = bisect(data, x0, 1)
      const d0 = data[i - 1]
      const d1 = data[i]
      if (!d0 || !d1) return
      const d = x0 - d0.date > d1.date - x0 ? d1 : d0

      tooltip
        .style('opacity', 1)
        .html(`<strong>${d3.timeFormat('%b %d, %Y')(d.date)}</strong><br/>Directions: ${d.directions}<br/>Calls: ${d.calls}<br/>Website: ${d.website}`)
        .style('left', `${x(d.date) + margin.left + 10}px`)
        .style('top', `${y(d.directions + d.calls + d.website) + margin.top - 10}px`)
    })
    .on('mouseleave', () => tooltip.style('opacity', 0))
}

const renderPieChart = () => {
  const container = pieChart.value
  if (!container) return

  d3.select(container).selectAll('*').remove()

  const totalSearch = metrics.value.reduce((s, m) => s + (m.total_search_impressions || 0), 0)
  const totalMaps = metrics.value.reduce((s, m) => s + (m.total_maps_impressions || 0), 0)

  if (totalSearch + totalMaps === 0) return

  const data = [
    { label: 'Search', value: totalSearch, color: COLORS.search },
    { label: 'Maps', value: totalMaps, color: COLORS.maps }
  ]

  const size = 220
  const radius = size / 2
  const innerRadius = radius * 0.55

  const svg = d3.select(container)
    .append('svg')
    .attr('width', size + 120)
    .attr('height', size)
    .append('g')
    .attr('transform', `translate(${radius}, ${radius})`)

  const pie = d3.pie().value((d) => d.value).sort(null).padAngle(0.03)
  const arc = d3.arc().innerRadius(innerRadius).outerRadius(radius).cornerRadius(4)

  const arcs = svg.selectAll('.arc')
    .data(pie(data))
    .join('g')
    .attr('class', 'arc')

  arcs.append('path')
    .attr('d', arc)
    .attr('fill', (d) => d.data.color)
    .attr('stroke', 'white')
    .attr('stroke-width', 2)
    .style('cursor', 'pointer')
    // eslint-disable-next-line func-names
    .on('mouseenter', function() { d3.select(this).transition().duration(150).attr('d', d3.arc().innerRadius(innerRadius).outerRadius(radius + 6).cornerRadius(4)) })
    // eslint-disable-next-line func-names
    .on('mouseleave', function() { d3.select(this).transition().duration(150).attr('d', arc) })

  // Center text
  const total = totalSearch + totalMaps
  svg.append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', '-0.3em')
    .attr('fill', '#111827')
    .attr('font-size', '20px')
    .attr('font-weight', '600')
    .text(total.toLocaleString())

  svg.append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', '1.2em')
    .attr('fill', '#6b7280')
    .attr('font-size', '11px')
    .text('Total')

  // Legend
  const legend = svg.append('g').attr('transform', `translate(${radius + 20}, -30)`)
  data.forEach((d, i) => {
    const g = legend.append('g').attr('transform', `translate(0, ${i * 30})`)
    g.append('rect').attr('width', 12).attr('height', 12).attr('rx', 3)
      .attr('fill', d.color)
    g.append('text').attr('x', 18).attr('y', 10).attr('fill', '#374151')
      .attr('font-size', '13px')
      .text(`${d.label} (${Math.round(d.value / total * 100)}%)`)
  })
}

const renderDeviceChart = () => {
  const container = deviceChart.value
  if (!container) return

  d3.select(container).selectAll('*').remove()

  const desktopSearch = metrics.value.reduce((s, m) => s + (m.business_impressions_desktop_search || 0), 0)
  const desktopMaps = metrics.value.reduce((s, m) => s + (m.business_impressions_desktop_maps || 0), 0)
  const mobileSearch = metrics.value.reduce((s, m) => s + (m.business_impressions_mobile_search || 0), 0)
  const mobileMaps = metrics.value.reduce((s, m) => s + (m.business_impressions_mobile_maps || 0), 0)

  const total = desktopSearch + desktopMaps + mobileSearch + mobileMaps
  if (total === 0) return

  const data = [
    { label: 'Desktop Search', value: desktopSearch, color: COLORS.desktopSearch },
    { label: 'Desktop Maps', value: desktopMaps, color: COLORS.desktopMaps },
    { label: 'Mobile Search', value: mobileSearch, color: COLORS.mobileSearch },
    { label: 'Mobile Maps', value: mobileMaps, color: COLORS.mobileMaps }
  ]

  const size = 220
  const radius = size / 2
  const innerRadius = radius * 0.55

  const svg = d3.select(container)
    .append('svg')
    .attr('width', size + 150)
    .attr('height', size)
    .append('g')
    .attr('transform', `translate(${radius}, ${radius})`)

  const pie = d3.pie().value((d) => d.value).sort(null).padAngle(0.03)
  const arc = d3.arc().innerRadius(innerRadius).outerRadius(radius).cornerRadius(4)

  const arcs = svg.selectAll('.arc')
    .data(pie(data))
    .join('g')
    .attr('class', 'arc')

  arcs.append('path')
    .attr('d', arc)
    .attr('fill', (d) => d.data.color)
    .attr('stroke', 'white')
    .attr('stroke-width', 2)
    .style('cursor', 'pointer')
    // eslint-disable-next-line func-names
    .on('mouseenter', function() { d3.select(this).transition().duration(150).attr('d', d3.arc().innerRadius(innerRadius).outerRadius(radius + 6).cornerRadius(4)) })
    // eslint-disable-next-line func-names
    .on('mouseleave', function() { d3.select(this).transition().duration(150).attr('d', arc) })

  // Center text
  const mobile = mobileSearch + mobileMaps
  const mobilePercent = Math.round(mobile / total * 100)

  svg.append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', '-0.3em')
    .attr('fill', '#111827')
    .attr('font-size', '20px')
    .attr('font-weight', '600')
    .text(`${mobilePercent}%`)

  svg.append('text')
    .attr('text-anchor', 'middle')
    .attr('dy', '1.2em')
    .attr('fill', '#6b7280')
    .attr('font-size', '11px')
    .text('Mobile')

  // Legend
  const legend = svg.append('g').attr('transform', `translate(${radius + 20}, -50)`)
  data.forEach((d, i) => {
    const g = legend.append('g').attr('transform', `translate(0, ${i * 26})`)
    g.append('rect').attr('width', 12).attr('height', 12).attr('rx', 3)
      .attr('fill', d.color)
    g.append('text').attr('x', 18).attr('y', 10).attr('fill', '#374151')
      .attr('font-size', '12px')
      .text(`${d.label} (${Math.round(d.value / total * 100)}%)`)
  })
}

const renderCharts = () => {
  if (!metrics.value.length) return
  renderImpressionsChart()
  renderEngagementChart()
  renderPieChart()
  renderDeviceChart()
}

const fetchMetrics = async() => {
  loading.value = true
  try {
    const response = await getLocationMetrics(props.locationId, { preset: selectedPreset.value })
    metrics.value = response.data.metrics || []
    summary.value = response.data.summary || {}
    comparison.value = response.data.comparison || {}
    await nextTick()
    renderCharts()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching metrics:', err)
  } finally {
    loading.value = false
  }
}

const selectDateRange = (preset) => {
  selectedPreset.value = preset
  fetchMetrics()
}

const syncMetrics = async() => {
  syncing.value = true
  try {
    await syncLocationMetrics(props.locationId)
    setTimeout(() => {
      fetchMetrics()
      syncing.value = false
    }, 5000)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error syncing metrics:', err)
    syncing.value = false
  }
}

onMounted(fetchMetrics)
</script>
